// 実ストア用のShopify APIクライアント
interface RealProduct {
  id: number;
  title: string;
  handle: string;
  variants: Array<{
    id: number;
    title: string;
    price: string;
    inventory_quantity: number;
  }>;
  images: Array<{
    id: number;
    src: string;
    alt: string;
  }>;
  tags: string | string[];
  product_type: string;
}

interface RealCartItem {
  productId: number;
  variantId: number;
  quantity: number;
  properties: Record<string, string>;
}

class RealShopifyAPI {
  private baseURL: string;

  constructor() {
    this.baseURL = '/api/shopify';
  }

  // 全商品を取得（ページネーション対応）
  async getAllProducts(): Promise<RealProduct[]> {
    try {
      console.log('🛍️ [REAL] Getting all products with pagination...');

      let allProducts: RealProduct[] = [];
      let pageInfo: string | null = null;
      let pageCount = 0;

      while (true) {
        pageCount++;
        console.log(`🛍️ [REAL] Fetching page ${pageCount}...`);

        // ページネーションパラメータを構築
        const url = pageInfo
          ? `${this.baseURL}/products.json?page_info=${encodeURIComponent(pageInfo)}`
          : `${this.baseURL}/products.json`;

        console.log(`🛍️ [REAL] Requesting URL: ${url}`);

        const response: Response = await fetch(url, {
          headers: {
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error(`商品の取得に失敗しました: ${response.status}`);
        }

        const data = await response.json();
        const products = data.products || [];
        allProducts = allProducts.concat(products);

        console.log(`🛍️ [REAL] Page ${pageCount}: ${products.length} products, Total: ${allProducts.length}`);

        // 次のページのpage_infoを取得
        const linkHeader: string | null = response.headers.get('link');
        if (linkHeader) {
          const nextMatch: RegExpMatchArray | null = linkHeader.match(/<[^>]*[?&]page_info=([^&>]+)[^>]*>;\s*rel="next"/);
          if (nextMatch) {
            pageInfo = nextMatch[1];
            console.log(`🛍️ [REAL] Next page_info: ${pageInfo}`);
          } else {
            console.log(`🛍️ [REAL] No next page found in link header: ${linkHeader}`);
            break;
          }
        } else {
          console.log(`🛍️ [REAL] No link header found, stopping pagination`);
          break;
        }
      }

      console.log(`🛍️ [REAL] All products loaded: ${allProducts.length} total`);

      // デバッグ: 最初の商品のタグ形式を確認
      if (allProducts.length > 0) {
        console.log('🛍️ [DEBUG] First product tags:', allProducts[0].tags, typeof allProducts[0].tags);
      }

      return allProducts;
    } catch (error) {
      console.error('Get all products error:', error);
      throw error;
    }
  }

  // 商品一覧を取得（実ストア）- 既存のメソッドは残す
  async getProducts(): Promise<RealProduct[]> {
    try {
      console.log('🛍️ [REAL] Getting products from photopri.myshopify.com...');

      const response = await fetch(`${this.baseURL}/products.json`, {
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`商品の取得に失敗しました: ${response.status}`);
      }

      const data = await response.json();
      console.log('🛍️ [REAL] Products loaded:', data.products?.length || 0);

      // デバッグ: 最初の商品のタグ形式を確認
      if (data.products && data.products.length > 0) {
        console.log('🛍️ [DEBUG] First product tags:', data.products[0].tags, typeof data.products[0].tags);
      }

      return data.products || [];
    } catch (error) {
      console.error('Get products error:', error);
      throw error;
    }
  }

  // 特定の商品を取得（実ストア）
  async getProduct(productId: number): Promise<RealProduct> {
    try {
      console.log(`🛍️ [REAL] Getting product ${productId}...`);

      const response = await fetch(`${this.baseURL}/products/${productId}.json`, {
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`商品の取得に失敗しました: ${response.status}`);
      }

      const data = await response.json();
      return data.product;
    } catch (error) {
      console.error('Get product error:', error);
      throw error;
    }
  }

  // カートに商品を追加（実ストア）
  async addToCart(item: RealCartItem): Promise<any> {
    try {
      console.log('🛍️ [REAL] Adding to cart:', item);

      const response = await fetch(`${this.baseURL}/orders.json`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          order: {
            line_items: [{
              product_id: item.productId,
              variant_id: item.variantId,
              quantity: item.quantity,
              properties: Object.entries(item.properties).map(([key, value]) => ({
                name: key,
                value: value
              }))
            }]
          }
        }),
      });

      if (!response.ok) {
        throw new Error(`カートへの追加に失敗しました: ${response.status}`);
      }

      const data = await response.json();
      console.log('🛍️ [REAL] Added to cart successfully');

      return data;
    } catch (error) {
      console.error('Add to cart error:', error);
      throw error;
    }
  }

  // 画像をアップロード（実ストア）
  async uploadImage(file: File): Promise<string> {
    try {
      console.log('🛍️ [REAL] Uploading image:', file.name);

      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${this.baseURL}/files.json`, {
        method: 'POST',
        // FormData sends its own Content-Type with boundary, so we don't set it manually
        // No auth header needed here as proxy handles it
      });

      if (!response.ok) {
        throw new Error(`画像のアップロードに失敗しました: ${response.status}`);
      }

      const data = await response.json();
      console.log('🛍️ [REAL] Image uploaded successfully');

      return data.file.url;
    } catch (error) {
      console.error('Upload image error:', error);
      throw error;
    }
  }

  // 注文を作成（実ストア）
  async createOrder(orderData: {
    lineItems: Array<{
      productId: number;
      variantId: number;
      quantity: number;
      properties: Record<string, string>;
    }>;
  }): Promise<any> {
    try {
      console.log('🛍️ [REAL] Creating order:', orderData);

      const response = await fetch(`${this.baseURL}/orders.json`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          order: {
            line_items: orderData.lineItems.map(item => ({
              product_id: item.productId,
              variant_id: item.variantId,
              quantity: item.quantity,
              properties: Object.entries(item.properties).map(([key, value]) => ({
                name: key,
                value: value
              }))
            }))
          }
        }),
      });

      if (!response.ok) {
        throw new Error(`注文の作成に失敗しました: ${response.status}`);
      }

      const data = await response.json();
      console.log('🛍️ [REAL] Order created successfully');

      return data;
    } catch (error) {
      console.error('Create order error:', error);
      throw error;
    }
  }

  // ストア情報を取得
  async getShopInfo(): Promise<any> {
    try {
      console.log('🛍️ [REAL] Getting shop info...');

      const response = await fetch(`${this.baseURL}/shop.json`, {
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`ストア情報の取得に失敗しました: ${response.status}`);
      }

      const data = await response.json();
      console.log('🛍️ [REAL] Shop info loaded');

      return data;
    } catch (error) {
      console.error('Get shop info error:', error);
      throw error;
    }
  }
}

export const realShopifyAPI = new RealShopifyAPI();
export type { RealProduct, RealCartItem };
